import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import * as _ from 'lodash';
import {
  BehaviorSubject,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Dao } from '../dao';
import { DataLoader } from '../data-loader';
import { DataLoaderAuthentified } from '../data-loader-authentified';

@Injectable({
  providedIn: 'root',
})
export class OptimizationDao extends Dao {
  private optimizationCache$: BehaviorSubject<any[]> = new BehaviorSubject(
    null,
  );

  private optimizationProperties = {
    LOAN_INTEREST_RATE: {
      style: { backgroundColor: '#3376F6', icon: 'euro' },
      label: value =>
        `Économisez <span>${parseInt(value)}€</span> sur vos crédits ! `,
      secondLabel:
        'Nous venons de détecter que votre crédit vous coute trop cher. Effectuez un rachat et ne payez que le nécessaire ! ',
      value: 0,
      subjectId: null,
    },
    LOAN_INSURANCE_RATE: {
      style: { backgroundColor: '#3376F6', icon: 'euro' },
      label: value =>
        `Économisez <span>${parseInt(
          value,
        )}€</span> sur vos assurances emprunteur ! `,
      secondLabel:
        'Nous venons de détecter que votre crédit vous coute trop cher. Effectuez un rachat et ne payez que le nécessaire ! ',
      value: 0,
      subjectId: null,
    },
    TAX: {
      style: { backgroundColor: '#FF827D', icon: 'euro' },
      label: value =>
        `Économisez <span>${parseInt(
          value,
        )}€</span> sur votre impôt sur le revenu !  découvrez comment le réduire`,
      secondLabel:
        'Découvrez une stratégie et des produits adaptés à votre situation pour réduire vos impôts ! ',
      value: 0,
    },
    BUY_OR_RENT: {
      style: { backgroundColor: '#FF827D', icon: 'euro' },
      label: value =>
        `Économisez <span>${parseInt(
          value,
        )}€</span> sur votre résidence principale !`,
      secondLabel:
        'Découvrez une stratégie et des placements adaptés à votre situation !',
      value: 0,
    },
    FISCALITY_RENT: {
      style: { backgroundColor: '#FF827D', icon: 'euro' },
      label: value =>
        `Économisez <span>${parseInt(
          value,
        )}€</span> sur l'imposition de votre bien !`,
      secondLabel: 'Découvrez quel régime fiscal est le plus optimisé',
      value: 0,
    },
  };

  private optimizations: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentified(), user => {
      return this.httpApi.put(`/accounts/${user.accountId}/optimizations`).pipe(
        switchMap((optimizations: any) => {
          return of(optimizations.optimizations);
        }),
      );
    });

  private profilEmprunteurReferences: DataLoader<any> = new DataLoader<any>(
    () => this.httpApi.get(`/ref/profil-emprunteur`),
  );

  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  public isOptimizationEnabled(): Observable<boolean> {
    return this.command(user =>
      this.httpApi.get(`/accounts/${user.accountId}/optimizations/enabled`),
    );
  }

  private fetchOptimizations(): Observable<any[]> {
    return this.optimizations
      .get()
      .pipe(tap(optimizations => this.optimizationCache$.next(optimizations)));
  }

  getOptimizationsValid(): Observable<any[]> {
    if (this.optimizationCache$.value) {
      return of(this.optimizationCache$.value).pipe(
        switchMap(optimizations => {
          let optim = optimizations?.filter(
            o => o?.value > 0 && !o?.fromEuodia && o.type != 'OBJECTIVES',
          );
          return of(optim);
        }),
      );
    } else {
      return this.fetchOptimizations().pipe(
        switchMap(optimizations => {
          let optim = optimizations?.filter(
            o => o?.value > 0 && !o?.fromEuodia && o.type != 'OBJECTIVES',
          );
          return of(optim);
        }),
      );
    }
  }

  getUnseenOptimizationsByTypeOnce(type: string): Observable<any[]> {
    return this.getOptimizationsValid().pipe(
      take(1),
      map(optimizations =>
        optimizations.filter(o => !o?.seen && o?.type === type),
      ),
    );
  }

  getOptimizationsUnseen(): Observable<any[]> {
    return this.getOptimizationsValid().pipe(
      map(optimizations =>
        optimizations.filter(
          optim => !optim.seen && optim.type != 'OBJECTIVES',
        ),
      ),
    );
  }

  getOptimizationObjective(forceRefresh: boolean = false): Observable<any[]> {
    if (forceRefresh) {
      this.reset();
      this.resetCache();
    }
    if (this.optimizationCache$.value) {
      return of(this.optimizationCache$.value).pipe(
        switchMap(optimizations => {
          let optim = optimizations?.filter(o => o.type == 'OBJECTIVES');
          return of(optim);
        }),
      );
    } else {
      return this.fetchOptimizations().pipe(
        switchMap(optimizations => {
          let optim = optimizations?.filter(o => o.type == 'OBJECTIVES');
          return of(optim);
        }),
      );
    }
  }

  getOptimizationsByType(type): Observable<any[]> {
    return this.getOptimizationsValid().pipe(
      switchMap(optimizations => {
        let optim = optimizations.filter(o => o?.type == type);
        return of(optim);
      }),
    );
  }

  getOptimizationById(id): Observable<any> {
    id = parseInt(id);
    return this.optimizations.get().pipe(
      switchMap(optimizations => {
        let optim = optimizations.find(o => o?.id === id);
        return of(optim);
      }),
    );
  }

  optimizeBuyOrRent(accountId: number, input: any): any {
    return this.httpApi.post(
      `/accounts/${accountId}/optimizations/buyOrRent`,
      input,
    );
  }

  updateOptimizationInsuranceRate(
    accountId: number,
    id: number,
    input: any,
  ): any {
    return this.httpApi.put(
      `/accounts/${accountId}/optimizations/${id}/insurance-rate`,
      input,
    );
  }

  optimizeFiscalityRent(accountId: number, input: any): Observable<any> {
    return this.httpApi.post(`/accounts/${accountId}/optimizations`, input);
  }

  markOptimizationAsRead(id: number, accountId: number): Observable<any> {
    return this.httpApi
      .put(`/accounts/${accountId}/optimizations/${id}/seen`)
      .pipe(
        switchMap(optimization => {
          return this.optimizations.current$().pipe(
            switchMap(optimizations => {
              let optim = optimizations.find(o => o?.id === id);
              optim.seen = true;
              return of(optim);
            }),
          );
        }),
      );
  }

  getOptimizationNotifications(): Observable<any> {
    return this.getOptimizationsValid().pipe(
      switchMap(optimizations => {
        let optimizationNotifications = [];
        const filteredOptimizations = optimizations?.filter(
          optimization => optimization.type !== 'OBJECTIVES',
        );

        filteredOptimizations?.forEach(optimization => {
          optimizationNotifications.push(
            this.formatOptimization(
              optimization.type,
              optimization.value,
              optimization.id,
              optimization.seen,
            ),
          );
        });

        optimizationNotifications = _.sortBy(
          optimizationNotifications,
          'creationDate',
        );

        return of(optimizationNotifications);
      }),
    );
  }

  getOptimizationSummary(): Observable<any> {
    return this.getOptimizationsValid().pipe(
      switchMap(optimizations => {
        const filteredOptimizations = optimizations?.filter(
          optimization => optimization.type !== 'OBJECTIVES',
        );
        let optimizationGrouped = {},
          optimizationSummary = [];
        filteredOptimizations.forEach(optimization => {
          if (!optimization.seen) {
            if (!optimizationGrouped[optimization.type]) {
              optimizationGrouped[optimization.type] = {
                ...this.optimizationProperties[optimization.type],
              };
            }

            optimizationGrouped[optimization.type].value += optimization.value;
          }
        });

        for (let key in optimizationGrouped) {
          const optimizationFilteredType = filteredOptimizations.filter(
            e => e.type === key,
          );

          let max = _.maxBy(optimizationFilteredType, 'value');
          optimizationGrouped[key].subjectId = max.id;
          optimizationGrouped[key].seen = max.seen;
          optimizationSummary.push(
            this.formatOptimization(
              key,
              optimizationGrouped[key].value,
              optimizationGrouped[key].subjectId,
              optimizationGrouped[key].seen,
            ),
          );
        }

        return of(optimizationSummary);
      }),
    );
  }

  getProfilEmprunteurReferences(): Observable<any> {
    return this.profilEmprunteurReferences.get();
  }

  formatOptimization(key, value, subjectId, seen) {
    let optimization = this.optimizationProperties[key];
    return {
      type: key,
      ...optimization,
      subjectId: subjectId,
      seen: seen,
      label: optimization.label(value),
    };
  }

  /* state */
  onUserChange(event: any) {
    this?.reset();
  }

  reset() {
    this.optimizations?.reset();
  }
  resetCache() {
    this.optimizationCache$.next(null);
  }
}
